import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getGetCustomReportQueryKey,
  getListCustomReportsByCaseQueryKey,
  useCreateCustomReport,
  useGetTemplateList,
  useListCustomReportsByCase,
} from "~/api/custom-reports/custom-reports";
import { ReportListItem } from "~/api/model";
import { TemplateListItem } from "~/api/model/templateListItem";
import { trackEvent } from "~/utils/analytics";
import { FINAL_REPORT_STATUSES, generateIdempotencyKey } from "./reportUtils";

export type TemplateReport = {
  template: TemplateListItem | null;
  report: ReportListItem | null;
};

export const useTemplateReports = (
  organizationId: string,
  projectId: string
) => {
  const queryClient = useQueryClient();

  const [locallyGenerating, setLocallyGenerating] = useState<Set<string>>(
    new Set()
  );

  const { data: templatesData, isLoading: isTemplatesLoading } =
    useGetTemplateList(organizationId, {
      query: { staleTime: 30_000 },
    });

  const { data: reportsData, isLoading: isReportsLoading } =
    useListCustomReportsByCase(organizationId, projectId, {
      query: {
        refetchInterval: (query) => {
          if (!query.state.data) {
            return false;
          }

          const reports = query.state.data.reportList ?? [];
          const needsPolling = reports.some(
            (report) =>
              !FINAL_REPORT_STATUSES.includes(
                report.status as (typeof FINAL_REPORT_STATUSES)[number]
              )
          );

          return needsPolling ? 15_000 : false;
        },
        refetchIntervalInBackground: true,
      },
    });

  const prevStatusesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const list = reportsData?.reportList ?? [];
    const prev = prevStatusesRef.current;
    const next: Record<string, string> = {};

    for (const r of list) {
      next[r.id] = r.status;
      const isFinalStatus = FINAL_REPORT_STATUSES.includes(
        r.status as (typeof FINAL_REPORT_STATUSES)[number]
      );
      const wasFinalStatus =
        prev[r.id] &&
        FINAL_REPORT_STATUSES.includes(
          prev[r.id] as (typeof FINAL_REPORT_STATUSES)[number]
        );

      if (isFinalStatus && prev[r.id] && !wasFinalStatus) {
        queryClient.invalidateQueries({
          queryKey: getGetCustomReportQueryKey(organizationId, projectId, r.id),
        });
      }
    }

    prevStatusesRef.current = next;
  }, [organizationId, projectId, reportsData?.reportList, queryClient]);

  const templateReports = useMemo<TemplateReport[]>(() => {
    const templates = templatesData?.templateList ?? [];

    const result: TemplateReport[] = templates
      .filter((template) => !template.isDraft)
      .sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return 0;
      })
      .map((template) => ({
        template,
        report: null,
      }));

    const reports = reportsData?.reportList ?? [];

    reports.forEach((report) => {
      const existingTempRep = result.find(
        (templateReport) => templateReport.template?.name === report.title
      );

      if (existingTempRep) {
        existingTempRep.report = report;
      }
    });

    return result;
  }, [templatesData, reportsData]);

  const { mutate: createReport } = useCreateCustomReport({
    mutation: {
      onSuccess: async () => {
        await queryClient.resetQueries({
          queryKey: getListCustomReportsByCaseQueryKey(
            organizationId,
            projectId
          ),
        });
      },
      onError: (_error, variables) => {
        setLocallyGenerating((prev) => {
          const next = new Set(prev);
          next.delete(variables.data.templateId);
          return next;
        });
      },
    },
  });

  useEffect(() => {
    const reports = reportsData?.reportList ?? [];
    const templates = templatesData?.templateList ?? [];

    setLocallyGenerating((prev) => {
      const next = new Set(prev);
      let changed = false;

      prev.forEach((templateId) => {
        const template = templates.find((t) => t.id === templateId);
        if (!template) return;

        const report = reports.find((r) => r.title === template.name);

        if (
          report &&
          FINAL_REPORT_STATUSES.includes(
            report.status as (typeof FINAL_REPORT_STATUSES)[number]
          )
        ) {
          next.delete(templateId);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [reportsData?.reportList, templatesData?.templateList]);

  const handleGenerateReport = useCallback(
    (templateId: string) => {
      if (!templateId) return;
      const template = (templatesData?.templateList ?? []).find(
        (t) => t.id === templateId
      );
      trackEvent({
        eventName: "app/report/generate.button/click",
        properties: {
          templateId,
          templateName: template?.name ?? "unknown",
        },
      });
      setLocallyGenerating((prev) => new Set(prev).add(templateId));
      createReport({
        organizationId,
        projectId,
        data: {
          idempotencyKey: generateIdempotencyKey(),
          templateId,
        },
      });
    },
    [createReport, organizationId, projectId, templatesData?.templateList]
  );

  return {
    templateReports,
    locallyGenerating,
    handleGenerateReport,
    isTemplatesLoading,
    isReportsLoading,
  };
};
